/**
 * Upload methods for FileStorageService
 */

import {
  FileCategory,
  UploadResponse,
  AutoUploadResponse,
} from '../../models/FileStorage';
import { API_ENDPOINTS } from '../../config/api';
import { apiClient } from '../api/apiClient';
import { FILE_OPERATION_TIMEOUT } from './constants';

/**
 * Auto-upload map file (fire-and-forget pattern)
 * Returns 202 Accepted immediately, actual upload happens in background
 *
 * @param projectId - Project identifier
 * @param file - Map JSON file to upload
 * @returns Auto-upload response with 202 status
 */
export async function autoUploadMap(
  projectId: string,
  file: File
): Promise<AutoUploadResponse> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const endpoint = API_ENDPOINTS.FILE_STORAGE.AUTO_UPLOAD_MAP(projectId);


    const response = await apiClient.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minutes for auto-upload
    });

    return response.data;
  } catch (error) {
    console.error('[FileStorageService] Auto-upload failed:', error);
    throw error;
  }
}

/**
 * Upload files with optional progress callback
 *
 * @param projectId - Project identifier
 * @param category - File category (map, cad, roi, learning, test)
 * @param files - Array of files to upload
 * @param onProgress - Optional callback for upload progress (0-100)
 * @param mapId - Optional map/lot ID for map-scoped storage ({projectId}/uploads/{mapId}/{category})
 * @returns Upload response with uploaded file information
 */
export async function uploadFiles(
  projectId: string,
  category: FileCategory,
  files: File[],
  onProgress?: (progress: number) => void,
  mapId?: string
): Promise<UploadResponse> {
  try {
    const baseEndpoint = API_ENDPOINTS.FILE_STORAGE.UPLOAD(projectId, category, mapId);
    const totalFiles = files.length;

    // 청크 크기: 500개씩 나눠서 업로드
    const CHUNK_SIZE = 500;
    const chunks = [];
    for (let i = 0; i < files.length; i += CHUNK_SIZE) {
      chunks.push(files.slice(i, i + CHUNK_SIZE));
    }

    let totalSuccess = 0;
    let totalFailed = 0;
    let uploadedFiles: any[] = [];

    // Group files by their folder path (from webkitRelativePath)
    // Go's multipart strips path from filename (filepath.Base), so we use folder_path query param
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];

      // Group files by their relative directory path
      const filesByFolder = new Map<string, File[]>();
      chunk.forEach(file => {
        const relativePath = (file as any).webkitRelativePath;
        if (relativePath) {
          // "saved_B2_B3/CCTV_01/image.jpg" → folder = "saved_B2_B3/CCTV_01"
          const lastSlash = relativePath.lastIndexOf('/');
          const folderPath = lastSlash > 0 ? relativePath.substring(0, lastSlash) : '';
          const existing = filesByFolder.get(folderPath) || [];
          existing.push(file);
          filesByFolder.set(folderPath, existing);
        } else {
          const existing = filesByFolder.get('') || [];
          existing.push(file);
          filesByFolder.set('', existing);
        }
      });

      // Upload each folder group with its folder_path
      for (const [folderPath, folderFiles] of filesByFolder) {
        const formData = new FormData();
        folderFiles.forEach(file => formData.append('files', file));

        const separator = baseEndpoint.includes('?') ? '&' : '?';
        const endpoint = folderPath
          ? `${baseEndpoint}${separator}folder_path=${encodeURIComponent(folderPath)}`
          : baseEndpoint;

      try {
        const response = await apiClient.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: FILE_OPERATION_TIMEOUT,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const chunkProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const overallProgress = Math.round(
                ((chunkIndex * CHUNK_SIZE + (chunk.length * chunkProgress / 100)) / totalFiles) * 100
              );
              onProgress(overallProgress);
            }
          },
        });

        if (response.data.success) {
          totalSuccess += response.data.data?.success_count || chunk.length;
          totalFailed += response.data.data?.failed_count || 0;
          if (response.data.data?.uploaded_files) {
            uploadedFiles.push(...response.data.data.uploaded_files);
          }
        }
      } catch (error) {
        console.error(`[FileStorageService] 청크 업로드 실패:`, error);
        totalFailed += folderFiles.length;
      }
      } // end filesByFolder loop
    } // end chunks loop

    // 최종 응답 반환 (마지막 청크 응답 형식 유지)
    return {
      success: totalSuccess > 0,
      message: `${totalSuccess}개 파일 업로드 성공${totalFailed > 0 ? `, ${totalFailed}개 실패` : ''}`,
      data: {
        success_count: totalSuccess,
        failed_count: totalFailed,
        total_files: totalFiles,
        uploaded_files: uploadedFiles,
      },
    };
  } catch (error) {
    console.error('[FileStorageService] Upload failed:', error);
    throw error;
  }
}
